import React from 'react';

import i18n from 'util/i18n';

import { HISTORY, HISTORY_EDUCATION } from 'config/formSections/history';

import Accordion, { openState } from 'components/Form/Accordion/Accordion';

import connectSubsection from 'components/Section/shared/SubsectionConnector';
import Subsection from 'components/Section/shared/Subsection';

import { EmptyListInstance } from 'forms/shared/models';
import { getDuplicateNamesAndAddresses } from 'helpers/people';
import ErrorMessage from 'components/ErrorMessage';
import { educationAddressNameFields } from 'constants/enums/people';
import Field from 'components/Form/Field';
import { isPVQForm } from 'helpers/form';
import EducationItem from './EducationItem';
import { EducationCustomSummary } from '../summaries';

const sectionConfig = {
  key: HISTORY_EDUCATION.key,
  section: HISTORY.name,
  store: HISTORY.store,
  subsection: HISTORY_EDUCATION.name,
  storeKey: HISTORY_EDUCATION.storeKey,
};

const byline = (item, index, initial, translation, required, isValid) => {
  switch (true) {
    // If item is required and not currently opened and is not valid, show message
    case required && !item.open && !isValid:
    case !item.open && !initial && item.Item && !isValid:
      return (
        <div className={`byline ${openState(item, initial)} fade in`.trim()}>
          <div className="usa-alert usa-alert-error" role="alert">
            <div className="usa-alert-body">
              <h5 className="usa-alert-heading">{i18n.m(translation)}</h5>
            </div>
          </div>
        </div>
      );

    default:
      return null;
  }
};

export class Education extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.handleError = this.handleError.bind(this);
  }

  customEducationByline = (item, index, initial) => {
    const { required, errors, overrideInitial } = this.props;

    const newInitial = overrideInitial ? false : initial;
    const itemHasErrors = errors && errors.filter((e) => e.indexOf(item.uuid) > -1).length > 0;

    return byline(
      item,
      index,
      newInitial,
      'history.education.collection.school.summary.incomplete',
      required,
      !itemHasErrors
    );
  };

  render() {
    const {
      totalYears, errors, List, peopleWhoKnowYou
    } = this.props;
    const accordionErrors = errors && errors.filter((e) => e.indexOf('List.accordion') === 0);

    const duplicatePeopleWhoKnowYou = getDuplicateNamesAndAddresses(List?.items, peopleWhoKnowYou, educationAddressNameFields);
    const duplicatePeopleWhoKnowYouError = (
      <Field
        className="no-margin-bottom"
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
        scrollIntoView={this.props.scrollIntoView}
        hideTitle
      >
        <ErrorMessage
          title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
          message={[
            i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.message'),
            ...duplicatePeopleWhoKnowYou.errors.map((item) => ` - ${item}`)
          ]}
        />
      </Field>
    );

    const duplicatePeopleWhoKnowYouWarning = (
      <ErrorMessage
        isWarning
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.title')}
        message={[
          i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.message'),
          ...duplicatePeopleWhoKnowYou.warnings.map((item) => ` - ${item}`)
        ]}
      />
    );

    const isPvq = isPVQForm(this.props.formType);
    const pvqAccordianList = this.props.isHistoricalDegree ? this.props.ListDegrees : this.props.List;
    const pvqAccordianLabelSummary = this.props.isHistoricalDegree ? i18n.t('history.pvq.education.collection.school.summary.degreeTitle') : i18n.t('history.pvq.education.collection.school.summary.title');
    const pvqAccordianLabelAppendTitle = this.props.isHistoricalDegree ? i18n.t('history.pvq.education.collection.school.degreeAppendTitle', {
      years: totalYears,
    }) : i18n.t('history.pvq.education.collection.school.appendTitle', {
      years: totalYears,
    });
    const pvqAccordianLabelAppend = this.props.isHistoricalDegree ? i18n.t('history.pvq.education.collection.school.degreeAppend') : i18n.t('history.pvq.education.collection.school.append');
    const attribute = this.props.isHistoricalDegree ? 'ListDegrees' : 'List';
    return isPvq ? (
      <div>
        <Accordion
          scrollToTop={this.props.scrollToTop}
          defaultState={this.props.defaultState}
          {...pvqAccordianList}
          sort={this.props.sort}
          realtime={this.props.realtime}
          onUpdate={this.props.onUpdate}
          onError={this.handleError}
          caption={this.props.caption}
          byline={this.customEducationByline}
          customSummary={EducationCustomSummary}
          description={pvqAccordianLabelSummary}
          appendTitle={pvqAccordianLabelAppendTitle}
          appendLabel={pvqAccordianLabelAppend}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          errors={accordionErrors}
          attribute={attribute}
          additionalErrors={
            (duplicatePeopleWhoKnowYou.errors.length > 0 && duplicatePeopleWhoKnowYouError)
            || (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
          }
        >
          <EducationItem
            bind
            name="Item"
            addressBooks={this.props.addressBooks}
            dispatch={this.props.dispatch}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            requireHistoryEducationReferenceFields={
              this.props.requireHistoryEducationReferenceFields
            }
            formType={this.props.formType}
            isHistoricalDegree={this.props.isHistoricalDegree}
          />
        </Accordion>
      </div>
    ) : (
      <div>
        <Accordion
          scrollToTop={this.props.scrollToTop}
          defaultState={this.props.defaultState}
          {...this.props.List}
          sort={this.props.sort}
          realtime={this.props.realtime}
          onUpdate={this.props.onUpdate}
          onError={this.handleError}
          caption={this.props.caption}
          byline={this.customEducationByline}
          customSummary={EducationCustomSummary}
          description={i18n.t('history.education.collection.school.summary.title')}
          appendTitle={i18n.t('history.education.collection.school.appendTitle', {
            years: totalYears,
          })}
          appendLabel={i18n.t('history.education.collection.school.append')}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          errors={accordionErrors}
          additionalErrors={
            (duplicatePeopleWhoKnowYou.errors.length > 0 && duplicatePeopleWhoKnowYouError)
            || (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
          }
        >
          <EducationItem
            bind
            name="Item"
            addressBooks={this.props.addressBooks}
            dispatch={this.props.dispatch}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            requireHistoryEducationReferenceFields={
              this.props.requireHistoryEducationReferenceFields
            }
            formType={this.props.formType}
          />
        </Accordion>
      </div>
    );
  }
}

Education.defaultProps = {
  List: EmptyListInstance,
  scrollIntoView: false,
  scrollToTop: '',
  defaultState: true,
  realtime: false,
  sort: null,
  totalYears: 10,
  overrideInitial: false,
  caption: null,
  formType: '',
  onUpdate: () => { },
  onError: (value, arr) => arr,
  section: 'history',
  subsection: 'education',
  dispatch: () => { },
  errors: [],
  isHistoricalDegree: false,
};

export default connectSubsection(Education, sectionConfig);
